/**
 * Graceful no-results surface. B-117 (2026-06-18).
 *
 * Renders when:
 *   - /compare streaming search completes with success_count === 0
 *     (every retailer was dropped by extraction or validators).
 *   - /c/<slug> cache returns 0 retailers (rare, e.g. a slug that
 *     was published before a retailer outage and never refreshed).
 *
 * Pattern: honest acknowledgement + one softer query the user can
 * try in a single click + a popular-searches escape hatch. No fake
 * spinner, no apologetic block of error logs. Soft amber accent so
 * the page reads as "we couldn't match THIS one" not "Mio is broken"
 * (NN #14 / Risk #13 — failure surfaces stay quiet on the brand).
 */

"use client";

import Link from "next/link";
import { useMemo } from "react";
import type { Route } from "next";

import { SearchAgainBar } from "@/components/comparison/SearchAgainBar";

// Hand-curated escape hatch for when the user's own query won't
// resolve. Drawn from the agent flagships fallback list (current AU
// flagships, June 2026). Refreshed manually with the flagships cron
// roll — no live fetch on this surface so the page renders fast even
// when the API is down.
const POPULAR_QUERIES: ReadonlyArray<{ label: string; q: string }> = [
  { label: "Apple iPhone 17", q: "Apple iPhone 17" },
  { label: "Apple iPhone 17 Pro", q: "Apple iPhone 17 Pro" },
  { label: "Samsung Galaxy S26 Ultra", q: "Samsung Galaxy S26 Ultra" },
  { label: "Google Pixel 10 Pro", q: "Google Pixel 10 Pro" },
  { label: "Sony WH-1000XM5", q: "Sony WH-1000XM5" },
  { label: "Apple Watch Series 11", q: "Apple Watch Series 11" },
];

interface Props {
  /** The user's verbatim search query. Pre-fills SearchAgainBar. */
  query: string;
  /** Optional cleaner label from clarify / parsed_intent. */
  productLabel?: string;
  /** Number of retailers Mio attempted (0 means we never got that far). */
  attempts?: number;
  /** Optional override copy for the secondary explanation line. */
  reasonHint?: string;
}

function stripVariantSpec(query: string): string | null {
  // Drop trailing storage / size / capacity / inch / litre tokens that
  // commonly cause the validator to reject otherwise-correct retailer
  // pages (e.g. "Apple iPhone 17 128GB" -> "Apple iPhone 17"). When
  // the stripped query is identical to the input, return null so the
  // caller knows there's no softer-query suggestion to offer.
  const cleaned = query
    .replace(
      /\s*\b\d+(?:\.\d+)?\s*(?:gb|tb|mb|mm|cm|inch|in|"|l|ml|w|kw)\b/gi,
      "",
    )
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return null;
  if (cleaned.toLowerCase() === query.trim().toLowerCase()) return null;
  return cleaned;
}

export function ComparisonNotFound({
  query,
  productLabel,
  attempts = 0,
  reasonHint,
}: Props) {
  const softerQuery = useMemo(() => stripVariantSpec(query), [query]);
  const headline = productLabel || query.trim() || "that";

  return (
    <main className="cmp-surface relative mx-auto min-h-screen max-w-4xl bg-background px-5 pb-16 sm:px-8">
      <SearchAgainBar initialQuery={query} />

      <section className="mx-auto mt-10 max-w-2xl text-center">
        <p className="mb-4 text-[12px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
          No matching retailers
        </p>
        <h1 className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">
          We couldn&apos;t find{" "}
          <span className="text-foreground">&ldquo;{headline}&rdquo;</span>{" "}
          right now.
        </h1>
        <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
          {reasonHint ??
            (attempts > 0
              ? `We checked ${attempts} ${attempts === 1 ? "retailer" : "retailers"} and none returned a clean product match.`
              : "We didn't find a matching product page on any of our Australian retailers.")}{" "}
          It may be too new, out of stock everywhere, or worded a little
          differently than the retailer pages.
        </p>
      </section>

      {softerQuery ? (
        <section className="mx-auto mt-8 max-w-2xl">
          <Link
            href={`/compare?q=${encodeURIComponent(softerQuery)}` as Route}
            className="group flex items-center justify-between gap-4 rounded-2xl border border-amber-500/30 bg-amber-500/5 px-5 py-4 text-left transition hover:border-amber-400/60 hover:bg-amber-500/10"
          >
            <span className="min-w-0">
              <span className="block text-[12px] font-semibold uppercase tracking-[0.08em] text-label-progress">
                Try a softer query
              </span>
              <span className="mt-1 block truncate text-base font-medium text-foreground">
                {softerQuery}
              </span>
              <span className="mt-1 block text-[13px] leading-snug text-muted-foreground">
                Retailer titles sometimes omit storage or size, so dropping the
                spec often surfaces the page.
              </span>
            </span>
            <span
              aria-hidden="true"
              className="shrink-0 text-amber-300/70 transition group-hover:translate-x-1"
            >
              →
            </span>
          </Link>
        </section>
      ) : null}

      <section className="mx-auto mt-10 max-w-2xl">
        <p className="mb-3 text-[12px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Popular searches
        </p>
        <div className="flex flex-wrap gap-2">
          {POPULAR_QUERIES.map((t) => (
            <Link
              key={t.q}
              href={`/compare?q=${encodeURIComponent(t.q)}` as Route}
              className="rounded-full border border-border/60 bg-secondary/30 px-3.5 py-1.5 text-[13px] font-medium text-foreground transition hover:border-accent/40 hover:bg-secondary/60"
            >
              {t.label}
            </Link>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-12 max-w-2xl text-center">
        <Link
          href={"/" as Route}
          className="inline-flex items-center gap-1.5 rounded-xl border border-border/60 bg-secondary/30 px-4 py-2.5 text-sm font-medium text-foreground backdrop-blur-md transition hover:border-accent/40 hover:bg-secondary/60"
        >
          ← Back to home
        </Link>
      </div>
    </main>
  );
}
